<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ToolboxItemSelect from './ToolboxItemSelect.vue';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  templates: {
    type: Array,
    default: () => [],
  },
  addedTemplateIds: {
    type: Array,
    default: () => [],
  },
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'configure'].includes(value),
  },
  procedure: {
    type: Object,
    default: null,
  },
  procedureIndex: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close', 'complete']);

const UNAVAILABLE_ROUTES = {
  spaces: 'settings_spaces_index',
  experiences: 'planning_experiences_index',
  knowledgebase: 'settings_knowledgebases_index',
};

const BASE_CONTACT_KEYS = ['name', 'email', 'phone_number'];
const BASE_EVENT_KEYS = ['pax', 'date', 'time_window'];

const DEFAULT_CONTACT_LABEL_KEYS = {
  name: 'CONTACTS_LAYOUT.FILTER.NAME',
  email: 'CONTACTS_LAYOUT.FILTER.EMAIL',
  phone_number: 'CONTACTS_LAYOUT.FILTER.PHONE_NUMBER',
};

const DEFAULT_EVENT_LABEL_KEYS = {
  pax: 'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.EVENT_FIELDS.PAX',
  date: 'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.EVENT_FIELDS.DATE',
  time_window:
    'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.EVENT_FIELDS.TIME_WINDOW',
};

function defaultContactAttributeLabel(key) {
  const i18nKey = DEFAULT_CONTACT_LABEL_KEYS[key];
  const translated = t(i18nKey);
  if (translated !== i18nKey) return translated;
  return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function defaultContactAttributeOptions() {
  return BASE_CONTACT_KEYS.map(key => ({
    value: key,
    label: defaultContactAttributeLabel(key),
    itemType: 'contact_attribute',
    icon: 'i-lucide-user',
    locked: true,
  }));
}

function defaultEventAttributeLabel(key) {
  const i18nKey = DEFAULT_EVENT_LABEL_KEYS[key];
  const translated = t(i18nKey);
  if (translated !== i18nKey) return translated;
  return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function defaultEventAttributeOptions() {
  return BASE_EVENT_KEYS.map(key => ({
    value: key,
    label: defaultEventAttributeLabel(key),
    itemType: 'event_attribute',
    icon: 'i-lucide-calendar',
    locked: false,
  }));
}

const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const showModal = computed({
  get: () => props.show,
  set: value => {
    if (!value) emit('close');
  },
});

const wizardStepIndex = ref(0);
const selectedTemplateId = ref(null);
const selectedContactKeys = ref([]);
const selectedEventKeys = ref([]);
const selectedLibraryIds = ref([]);

const selectedTemplate = computed(() =>
  props.templates.find(template => template.id === selectedTemplateId.value)
);

const hasContactConfigurable = computed(() =>
  (selectedTemplate.value?.steps || []).some(
    step => step.configurable === 'contact'
  )
);

const hasEventConfigurable = computed(() =>
  (selectedTemplate.value?.steps || []).some(
    step => step.configurable === 'event'
  )
);

const hasAttributesStep = computed(
  () => hasContactConfigurable.value || hasEventConfigurable.value
);

const hasLibrariesStep = computed(() =>
  (selectedTemplate.value?.steps || []).some(step => step.kb)
);

const wizardSteps = computed(() => {
  const steps = [];
  if (props.mode === 'add') {
    steps.push({
      id: 'template',
      title: t(
        'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.TEMPLATE.TITLE'
      ),
      body: t(
        'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.TEMPLATE.DESCRIPTION'
      ),
    });
  }
  if (hasAttributesStep.value) {
    steps.push({
      id: 'attributes',
      title: t(
        'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.TITLE'
      ),
      body: t(
        'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.DESCRIPTION'
      ),
    });
  }
  if (hasLibrariesStep.value) {
    steps.push({
      id: 'libraries',
      title: t(
        'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.LIBRARIES.TITLE'
      ),
      body: t(
        'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.LIBRARIES.DESCRIPTION'
      ),
    });
  }
  return steps;
});

const currentStep = computed(
  () => wizardSteps.value[wizardStepIndex.value] || null
);

const isLastStep = computed(
  () => wizardStepIndex.value >= wizardSteps.value.length - 1
);

const canProceed = computed(() => {
  if (!currentStep.value) return false;
  if (currentStep.value.id === 'template')
    return Boolean(selectedTemplateId.value);
  return true;
});

function ensureDefaultContactKeys(keys) {
  return [...new Set([...BASE_CONTACT_KEYS, ...keys])];
}

function templateHasEventConfigurable(template) {
  return (template?.steps || []).some(step => step.configurable === 'event');
}

function templateHasContactConfigurable(template) {
  return (template?.steps || []).some(step => step.configurable === 'contact');
}

const contactAttributeOptions = computed(() => {
  const records = store.getters['attributes/getAllContactAttributes'] || [];
  const customOptions = records
    .map(attr => ({
      value: attr.attributeKey || attr.attribute_key,
      label:
        attr.attributeDisplayName ||
        attr.attribute_display_name ||
        attr.attributeKey ||
        attr.attribute_key,
      itemType: 'contact_attribute',
      icon: 'i-lucide-user',
    }))
    .filter(attr => attr.value && !BASE_CONTACT_KEYS.includes(attr.value));

  if (!hasContactConfigurable.value) return customOptions;
  return [...defaultContactAttributeOptions(), ...customOptions];
});

const eventAttributeOptions = computed(() => {
  const records =
    store.getters['attributes/getAttributesByModel']('event_attribute') || [];
  const customOptions = records
    .map(attr => ({
      value: attr.attribute_key || attr.attributeKey,
      label:
        attr.attribute_display_name ||
        attr.attributeDisplayName ||
        attr.attribute_key ||
        attr.attributeKey,
      itemType: 'event_attribute',
      icon: 'i-lucide-calendar',
    }))
    .filter(attr => attr.value && !BASE_EVENT_KEYS.includes(attr.value));

  if (!hasEventConfigurable.value) return customOptions;
  return [...defaultEventAttributeOptions(), ...customOptions];
});

const libraryOptions = computed(() => {
  const libraries = selectedTemplate.value?.libraries || [];
  return libraries.map(kb => ({
    value: Number(kb.id),
    label: kb.name,
    itemType: 'library',
    icon: libraryIcon(kb.type),
    namespace: kb.namespace,
  }));
});

function libraryIcon(type) {
  const iconMap = {
    catalog: 'i-lucide-layout-grid',
    gallery: 'i-lucide-book-image',
    documents: 'i-lucide-file',
  };
  return iconMap[type] || 'i-lucide-file';
}

function templateTitle(template) {
  const key = `ASSISTANT_MGMT.PROCEDURES.STANDARD.TEMPLATES.${template.id}`;
  const translated = t(key);
  return translated === key ? template.name : translated;
}

function isTemplateAdded(template) {
  if (props.mode === 'configure' && template.id === selectedTemplateId.value) {
    return false;
  }
  return props.addedTemplateIds.includes(template.id);
}

function isTemplateUnavailable(template) {
  return template.available === false;
}

function isTemplateDisabled(template) {
  return isTemplateAdded(template) || isTemplateUnavailable(template);
}

function unavailableLabel(template) {
  const reason = template.unavailable_reason;
  if (!reason) return '';
  const key = `ASSISTANT_MGMT.PROCEDURES.STANDARD.UNAVAILABLE.${reason.toUpperCase()}`;
  const translated = t(key);
  return translated === key ? '' : translated;
}

function unavailableRoute(template) {
  const name = UNAVAILABLE_ROUTES[template.unavailable_reason];
  return name ? accountScopedRoute(name) : null;
}

function isUnavailableLink(template) {
  return isTemplateUnavailable(template) && Boolean(unavailableRoute(template));
}

function defaultSelectedLibraryIds(libraries) {
  return (libraries || [])
    .filter(kb => kb.type !== 'gallery')
    .map(kb => Number(kb.id))
    .filter(id => !Number.isNaN(id));
}

function selectTemplate(template) {
  if (isTemplateDisabled(template)) return;
  selectedTemplateId.value = template.id;
  selectedContactKeys.value = templateHasContactConfigurable(template)
    ? [...BASE_CONTACT_KEYS]
    : [];
  selectedEventKeys.value = templateHasEventConfigurable(template)
    ? [...BASE_EVENT_KEYS]
    : [];
  selectedLibraryIds.value = defaultSelectedLibraryIds(template.libraries);
}

function attachedKeysFromStep(step) {
  return (step?.attached_attributes || [])
    .map(item => (typeof item === 'string' ? item : item.key))
    .filter(Boolean);
}

function resetWizard() {
  wizardStepIndex.value = 0;
  if (props.mode === 'configure' && props.procedure) {
    const templateId =
      props.procedure.template_id || props.procedure.templateId;
    selectedTemplateId.value = templateId;
    const template = props.templates.find(t => t.id === templateId);
    const contactCatalogStep = (template?.steps || []).find(
      s => s.configurable === 'contact'
    );
    const eventCatalogStep = (template?.steps || []).find(
      s => s.configurable === 'event'
    );
    const contactProcStep = (props.procedure.steps || []).find(
      s => s.key === contactCatalogStep?.key
    );
    const eventProcStep = (props.procedure.steps || []).find(
      s => s.key === eventCatalogStep?.key
    );
    selectedContactKeys.value = ensureDefaultContactKeys(
      attachedKeysFromStep(contactProcStep)
    );
    selectedEventKeys.value = attachedKeysFromStep(eventProcStep).filter(
      key => key !== 'system_stage'
    );
    selectedLibraryIds.value = (
      props.procedure.knowledgebases ||
      props.procedure.libraries ||
      []
    )
      .map(kb => Number(kb.id ?? kb))
      .filter(id => !Number.isNaN(id));
  } else {
    selectedTemplateId.value = null;
    selectedContactKeys.value = [];
    selectedEventKeys.value = [];
    selectedLibraryIds.value = [];
  }
}

function closeModal() {
  showModal.value = false;
}

function goBack() {
  if (wizardStepIndex.value > 0) {
    wizardStepIndex.value -= 1;
  }
}

function goNext() {
  if (!canProceed.value) return;
  if (isLastStep.value) {
    submit();
    return;
  }
  wizardStepIndex.value += 1;
}

function submit() {
  if (!selectedTemplate.value) return;
  emit('complete', {
    template: selectedTemplate.value,
    contactAttributeKeys: hasContactConfigurable.value
      ? ensureDefaultContactKeys(selectedContactKeys.value)
      : [...selectedContactKeys.value],
    eventAttributeKeys: selectedEventKeys.value.filter(
      key => key !== 'system_stage'
    ),
    knowledgebaseIds: [...selectedLibraryIds.value],
    procedureIndex: props.procedureIndex,
    mode: props.mode,
  });
}

watch(
  () => props.show,
  async newValue => {
    if (newValue) {
      try {
        await store.dispatch('attributes/getAllContactAttributes');
        await store.dispatch('attributes/get');
      } catch {
        // Attributes may already be loaded by the toolbox.
      }
      resetWizard();
    }
  }
);

watch(selectedTemplateId, () => {
  // When template changes in add mode, rebuild wizard steps and clamp index.
  if (wizardStepIndex.value >= wizardSteps.value.length) {
    wizardStepIndex.value = Math.max(0, wizardSteps.value.length - 1);
  }
});
</script>

<template>
  <Modal v-model:show="showModal" size="large" @close="closeModal">
    <div class="p-8" data-testid="standard-procedure-wizard">
      <h2
        class="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-2"
      >
        {{
          mode === 'configure'
            ? $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.CONFIGURE_TITLE')
            : $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.TITLE')
        }}
      </h2>
      <p class="text-sm text-n-slate-10 mb-6">
        {{ $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.SUBTITLE') }}
      </p>

      <div class="flex gap-8 min-h-[420px]">
        <div class="w-64 flex-shrink-0">
          <div
            v-for="(step, index) in wizardSteps"
            :key="step.id"
            class="flex items-start gap-4 relative pb-8 last:pb-0"
          >
            <div
              class="rounded-2xl flex-shrink-0 size-8 border-2 border-n-slate-3 flex items-center justify-center z-10"
              :class="{
                'bg-n-slate-3': index <= wizardStepIndex,
                'bg-n-background': index > wizardStepIndex,
              }"
            >
              <Icon
                v-if="index < wizardStepIndex"
                icon="i-lucide-check"
                class="text-n-slate-11 size-4"
              />
              <span
                v-else
                class="text-xs font-bold"
                :class="
                  index === wizardStepIndex
                    ? 'text-woot-700'
                    : 'text-n-slate-11'
                "
              >
                {{ index + 1 }}
              </span>
            </div>
            <div class="flex flex-col gap-1 pt-1">
              <h3
                class="text-sm font-medium"
                :class="
                  index === wizardStepIndex
                    ? 'text-woot-700'
                    : 'text-n-slate-12'
                "
              >
                {{ step.title }}
              </h3>
              <p class="text-xs text-n-slate-11">{{ step.body }}</p>
            </div>
          </div>
        </div>

        <div
          class="flex-1 min-w-0 border-l border-n-slate-3 dark:border-n-slate-8 pl-8"
        >
          <div v-if="currentStep?.id === 'template'" class="space-y-3">
            <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
              {{
                $t(
                  'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.TEMPLATE.HEADING'
                )
              }}
            </h3>
            <component
              :is="isUnavailableLink(template) ? 'router-link' : 'button'"
              v-for="template in templates"
              :key="template.id"
              :to="
                isUnavailableLink(template)
                  ? unavailableRoute(template)
                  : undefined
              "
              :type="isUnavailableLink(template) ? undefined : 'button'"
              class="w-full text-left rounded-xl border p-4 transition-colors block"
              :class="
                isTemplateDisabled(template) && !isUnavailableLink(template)
                  ? 'border-n-slate-3 text-n-slate-8 cursor-not-allowed opacity-60'
                  : isUnavailableLink(template)
                    ? 'border-n-slate-3 text-n-slate-8 opacity-60 hover:border-n-slate-5'
                    : selectedTemplateId === template.id
                      ? 'border-woot-700 bg-woot-700/5'
                      : 'border-n-slate-4 hover:border-n-slate-6 hover:bg-n-slate-2 dark:hover:bg-n-slate-3'
              "
              :disabled="isTemplateAdded(template)"
              :data-testid="`wizard-template-${template.id}`"
              @click="selectTemplate(template)"
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="font-medium text-n-slate-12">
                    {{ templateTitle(template) }}
                  </p>
                  <p class="text-sm text-n-slate-10 mt-1">
                    {{ template.description }}
                  </p>
                </div>
                <span
                  v-if="isTemplateAdded(template)"
                  class="text-xs font-medium text-n-slate-8 whitespace-nowrap"
                >
                  {{ $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.ALREADY_ADDED') }}
                </span>
                <span
                  v-else-if="isTemplateUnavailable(template)"
                  class="text-xs font-medium text-woot-700 underline whitespace-nowrap"
                >
                  {{ unavailableLabel(template) }}
                </span>
                <i
                  v-else-if="selectedTemplateId === template.id"
                  class="i-lucide-check w-5 h-5 text-woot-700 flex-shrink-0"
                />
              </div>
            </component>
          </div>

          <div v-else-if="currentStep?.id === 'attributes'" class="space-y-6">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{
                $t(
                  'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.HEADING'
                )
              }}
            </h3>
            <p class="text-sm text-n-slate-10">
              {{
                $t(
                  'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.HELP'
                )
              }}
            </p>

            <div v-if="hasContactConfigurable" class="space-y-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  $t(
                    'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.CONTACT_LABEL'
                  )
                }}
              </label>
              <ToolboxItemSelect
                v-model="selectedContactKeys"
                :options="contactAttributeOptions"
                :placeholder="
                  $t(
                    'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.CONTACT_PLACEHOLDER'
                  )
                "
                :search-placeholder="
                  $t(
                    'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.SEARCH_PLACEHOLDER'
                  )
                "
                :empty-state="
                  $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.NO_ATTRIBUTES')
                "
              />
            </div>

            <div v-if="hasEventConfigurable" class="space-y-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{
                  $t(
                    'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.EVENT_LABEL'
                  )
                }}
              </label>
              <ToolboxItemSelect
                v-model="selectedEventKeys"
                :options="eventAttributeOptions"
                :placeholder="
                  $t(
                    'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.EVENT_PLACEHOLDER'
                  )
                "
                :search-placeholder="
                  $t(
                    'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.ATTRIBUTES.SEARCH_PLACEHOLDER'
                  )
                "
                :empty-state="
                  $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.NO_ATTRIBUTES')
                "
              />
            </div>
          </div>

          <div v-else-if="currentStep?.id === 'libraries'" class="space-y-4">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{
                $t(
                  'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.LIBRARIES.HEADING'
                )
              }}
            </h3>
            <p class="text-sm text-n-slate-10">
              {{
                $t(
                  'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.LIBRARIES.HELP'
                )
              }}
            </p>
            <ToolboxItemSelect
              v-model="selectedLibraryIds"
              :options="libraryOptions"
              :placeholder="
                $t(
                  'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.LIBRARIES.PLACEHOLDER'
                )
              "
              :search-placeholder="
                $t(
                  'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.LIBRARIES.SEARCH_PLACEHOLDER'
                )
              "
              :empty-state="
                $t(
                  'ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.STEPS.LIBRARIES.EMPTY'
                )
              "
            />
          </div>
        </div>
      </div>

      <div
        class="flex justify-between items-center mt-8 pt-6 border-t border-n-slate-3 dark:border-n-slate-8"
      >
        <NextButton
          ghost
          slate
          :label="$t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.BACK')"
          :disabled="wizardStepIndex === 0"
          @click="goBack"
        />
        <div class="flex items-center gap-2">
          <NextButton
            ghost
            slate
            :label="$t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.CANCEL')"
            @click="closeModal"
          />
          <NextButton
            solid
            blue
            data-testid="wizard-next-button"
            :label="
              isLastStep
                ? mode === 'configure'
                  ? $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.SAVE')
                  : $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.ADD')
                : $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.NEXT')
            "
            :disabled="!canProceed"
            @click="goNext"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
:deep(.modal-container) {
  @apply bg-white dark:bg-n-solid-2;
}
</style>
